import {
  CustomerGroupType,
  CustomerSegment,
  IamGroupType,
  PersonalAccountPurpose,
  PersonalAccountPurposeLabels,
  ReleaseChannel,
  UserGroupType,
  UserGroupTypeLabels
} from '@navio/nsdk';
import type { NoddiIconName } from 'noddi-ui-common';
import { getLabelMapFromEntries } from './options/choiceOptions';
import { formatPhoneNumber } from './phone/phone';

export const USER_GROUP_TYPE_ICON_MAP: Record<UserGroupType, NoddiIconName> = {
  enterprise_group: 'Globe',
  operation_layer: 'Route',
  service_organization: 'BriefcaseBusiness',
  service_department: 'Store',
  organization: 'Building2',
  group: 'Users',
  personal: 'User'
};

export const CUSTOMER_SEGMENT_ICON_MAP: Record<CustomerSegment, NoddiIconName> = {
  [CustomerSegment.ALL]: 'Filter',
  [CustomerSegment.PROSPECTS]: 'Search',
  [CustomerSegment.NEW_CUSTOMER]: 'WandSparkles',
  [CustomerSegment.ACTIVE_CLIENTS]: 'CircleUserRound',
  [CustomerSegment.VIP]: 'Star',
  [CustomerSegment.AT_RISK]: 'TriangleAlert',
  [CustomerSegment.CHURNED]: 'UserMinus',
  [CustomerSegment.FLEET_MANAGER]: 'Car'
};

export const RELEASE_CHANNEL_ICON_MAP: Record<ReleaseChannel, NoddiIconName> = {
  [ReleaseChannel.ALPHA]: 'Zap',
  [ReleaseChannel.BETA]: 'Activity',
  [ReleaseChannel.STABLE]: 'ShieldCheck'
};

const isUserGroupType = (s: string): s is UserGroupType => s in USER_GROUP_TYPE_ICON_MAP;

export type UserGroupTypeInput = {
  type?: UserGroupType | CustomerGroupType | IamGroupType | { value: string } | null;
};

export const getUserGroupTypeIcon = (type: UserGroupType | CustomerGroupType | IamGroupType): NoddiIconName =>
  USER_GROUP_TYPE_ICON_MAP[type] ?? 'User';

export const resolveUserGroupType = (userGroup: UserGroupTypeInput): UserGroupType => {
  const { type } = userGroup;
  if (type == null) {
    return 'personal';
  }
  if (typeof type === 'object') {
    return isUserGroupType(type.value) ? type.value : 'personal';
  }
  return isUserGroupType(type) ? type : 'personal';
};

const USER_GROUP_TYPE_LABEL_MAP = getLabelMapFromEntries(UserGroupType, UserGroupTypeLabels);

export const getUserGroupTypeLabel = (userGroup: UserGroupTypeInput): string =>
  USER_GROUP_TYPE_LABEL_MAP[resolveUserGroupType(userGroup)];

const PERSONAL_ACCOUNT_PURPOSE_LABEL_MAP = getLabelMapFromEntries(PersonalAccountPurpose, PersonalAccountPurposeLabels);

export const getPersonalAccountPurposeLabel = (purpose: PersonalAccountPurpose | null | undefined): string | null =>
  purpose == null ? null : (PERSONAL_ACCOUNT_PURPOSE_LABEL_MAP[purpose] ?? null);

const PERSONAL_ACCOUNT_PURPOSE_ICON_MAP: Record<PersonalAccountPurpose, NoddiIconName> = {
  [PersonalAccountPurpose.PRIVATE]: 'CircleUserRound',
  [PersonalAccountPurpose.BUSINESS]: 'BriefcaseBusiness'
};

export const getPersonalAccountPurposeIcon = (
  purpose: PersonalAccountPurpose | null | undefined
): NoddiIconName | null => (purpose == null ? null : (PERSONAL_ACCOUNT_PURPOSE_ICON_MAP[purpose] ?? null));

type UserGroupContactInput = {
  isPersonal: boolean;
  users: Array<{ phoneNumber?: string | null; email?: string | null }>;
};

/** Contact lines for personal customers only — phone then email; never includes backend IDs. */
export const getUserGroupContactSecondaryLabels = (userGroup: UserGroupContactInput): string[] => {
  if (!userGroup.isPersonal) {
    return [];
  }

  const firstUser = userGroup.users[0];
  if (!firstUser) {
    return [];
  }

  const phone = firstUser.phoneNumber ? formatPhoneNumber(firstUser.phoneNumber) : undefined;
  const email = firstUser.email ?? undefined;

  return [phone, email].filter((value): value is string => Boolean(value));
};
